import React from "react";
import PropTypes from "prop-types";
import OSSTablePaginationActions from "./OSSTablePaginationActions/OSSTablePaginationActions";
import Humanize from "humanize-plus";

import withStyles from "@material-ui/core/styles/withStyles";
import TableCell from "@material-ui/core/TableCell";
import MenuItem from "@material-ui/core/MenuItem";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";

const styles = theme => ({
  /* Styles applied to the root element. */
  root: {
    fontSize: theme.typography.pxToRem(12),
    // Increase the specificity to override TableCell.
    "&:last-child": {
      padding: 0
    }
  },
  /* Styles applied to the Toolbar component. */
  toolbar: {
    height: theme.spacing(6),
    minHeight: theme.spacing(6),
    padding: [[0, theme.spacing(2)]],
    overflowX: "auto"
  },
  /* Styles applied to the spacer element. */
  spacer: {
    flex: "1 1 100%"
  },
  /* Styles applied to the caption Typography components if `variant="caption"`. */
  caption: {
    flexShrink: 0
  },
  /* Styles applied to the Select component `root` class. */
  selectRoot: {
    marginRight: 24,
    marginLeft: 8
  },
  /* Styles applied to the Select component `select` class. */
  select: {
    paddingLeft: 8,
    paddingRight: 24,
    display: "flex",
    alignItems: "center",
    borderRadius: 4,
    "&:focus": {
      borderRadius: 4
    }
  },
  /* Styles applied to the Select component `icon` class. */
  selectIcon: {
    top: 1
  },
  /* Styles applied to the Input component. */
  input: {
    fontSize: "inherit",
    flexShrink: 0
  },
  /* Styles applied to the MenuItem component. */
  menuItem: {},
  labelDisplayedRows: {
    paddingRight: 16
  }
});

const OSSTablePagination = ({
  classes,
  colSpan: colSpanProp,
  component: Component,
  data,
  labelDisplayedRows,
  labelRowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  query,
  rowsPerPageOptions,
  SelectProps = {}
}) => {
  const handleChangePage = (e, page) => {
    onChangePage(page);
  };

  const handleChangeRowsPerPage = e => {
    onChangeRowsPerPage(e.target.value);
  };

  const MenuItemComponent = SelectProps.native ? "option" : MenuItem;

  let { offset, limit } = query;
  let { count } = data;
  let colSpan;

  if (Component === TableCell || Component === "td") {
    colSpan = colSpanProp || 1000; // col-span over everything
  }

  /* Need to coerce these to real numbers, otherwise the material-ui paginator component is not happy.*/
  offset = parseInt(offset, 10);
  limit = parseInt(limit, 10);
  count = parseInt(count, 10);
  const page = offset / limit;

  const from = count === 0 ? 0 : Humanize.intComma(page * limit + 1);
  const to = Humanize.intComma(Math.min(count, (page + 1) * limit));
  const total = Humanize.intComma(count);

  return (
    <>
      <Component className={classes.root} colSpan={colSpan}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.spacer} />
          {rowsPerPageOptions.length > 1 && (
            <Typography
              color="inherit"
              variant="caption"
              className={classes.caption}
            >
              {labelRowsPerPage}
            </Typography>
          )}
          {rowsPerPageOptions.length > 1 && (
            <Select
              classes={{
                root: classes.selectRoot,
                select: classes.select,
                icon: classes.selectIcon
              }}
              disableUnderline
              input={<Input className={classes.input} />}
              value={limit}
              onChange={handleChangeRowsPerPage}
              {...SelectProps}
            >
              {rowsPerPageOptions.map(rowsPerPageOption => (
                <MenuItemComponent
                  className={classes.menuItem}
                  key={rowsPerPageOption}
                  value={rowsPerPageOption}
                >
                  {rowsPerPageOption}
                </MenuItemComponent>
              ))}
            </Select>
          )}
          <Typography
            color="inherit"
            variant="caption"
            className={[classes.caption, classes.labelDisplayedRows].join(" ")}
          >
            {labelDisplayedRows({
              from,
              to,
              count: total,
              page
            })}
          </Typography>

          <OSSTablePaginationActions
            count={count}
            onChangePage={handleChangePage}
            page={page}
            rowsPerPage={limit}
            backIconButtonProps={{
              "aria-label": "Previous Page"
            }}
            nextIconButtonProps={{
              "aria-label": "Next Page"
            }}
          />
        </Toolbar>
      </Component>
    </>
  );
};

OSSTablePagination.propTypes = {
  backIconButtonProps: PropTypes.object,
  classes: PropTypes.object.isRequired,
  component: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.object
  ]),
  labelDisplayedRows: PropTypes.func,
  labelRowsPerPage: PropTypes.node,
  nextIconButtonProps: PropTypes.object,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  SelectProps: PropTypes.object
};

OSSTablePagination.defaultProps = {
  component: "div",
  labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} of ${count}`,
  labelRowsPerPage: "Rows per page:",
  rowsPerPageOptions: [3, 25, 50, 100]
};

export default withStyles(styles)(OSSTablePagination);
